import { Sun, Moon, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const steps = [
  {
    time: "Morning",
    icon: Sun,
    title: "Open your day",
    description: "Enter your starting cash balance",
    duration: "15 seconds",
    color: "from-accent to-accent-glow",
  },
  {
    time: "Evening",
    icon: Moon,
    title: "Close your day",
    description: "Log sales, expenses, ending cash",
    duration: "40 seconds",
    color: "from-primary to-primary-dark",
  },
  {
    time: "Instant",
    icon: Sparkles,
    title: "Know your profit",
    description: "Mismatch flagged. Profit calculated. Done.",
    duration: "5 seconds",
    color: "from-success to-primary",
  },
];

export const RitualSection = () => {
  return (
    <section id="ritual" className="relative py-20 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-secondary/20 via-background to-background" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 rounded-full blur-[120px]" />
      
      <div className="container relative z-10 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <div className="text-center space-y-4 mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">The Daily Ritual</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-balance px-2">
              60 Seconds.
              <br />
              <span className="text-gradient">Complete Clarity.</span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
              One simple ritual, every day. Know exactly how your business is doing.
            </p>
          </div>

          {/* Steps timeline */}
          <div className="relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-border to-transparent -translate-y-1/2" />
            
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className="relative group"
                >
                  {/* Card */}
                  <div className="relative p-6 md:p-8 rounded-2xl md:rounded-3xl bg-card border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1">
                    {/* Step number */}
                    <div className="absolute -top-3 left-6 md:left-8">
                      <span className="px-3 py-1 text-xs font-bold bg-card border border-border rounded-full">
                        {step.time.toUpperCase()}
                      </span>
                    </div>
                    
                    {/* Icon */}
                    <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <step.icon className="h-7 w-7 md:h-8 md:w-8 text-primary-foreground" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="font-display text-xl md:text-2xl font-semibold mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-4">
                      {step.description}
                    </p>
                    
                    {/* Duration badge */}
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-xs font-medium">
                      <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                      {step.duration}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total time callout */}
          <div className="mt-12 md:mt-16 text-center">
            <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl glass border border-accent/20">
              <div className="text-left">
                <div className="text-2xl md:text-3xl font-display font-bold text-accent">
                  Total: 60 seconds
                </div>
                <div className="text-sm text-muted-foreground">
                  That's less time than making chai ☕
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-10 text-center">
            <Button 
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="h-12 px-8 rounded-full border-2 hover:border-primary/50"
            >
              See All Features
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
